import { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { trackEvent } from "@/hooks/use-activity-tracker";
import { ErrorBoundary } from "@/components/error-boundary";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useScannerContext } from "@/contexts/use-scanner-context";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Upload, FileText, Image, AlertTriangle, CheckCircle2, Trash2,
  History, Eye, PlusCircle, Edit3, RefreshCw, ChevronDown, ChevronRight,
  ShieldCheck, ShieldAlert, ShieldOff, Plus, X,
} from "lucide-react";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer } from "recharts";
import {
  computeAllMetrics, entryToInputs,
  type ComputedMetrics,
} from "@/lib/floorplan-engine";
import { apiRequest } from "@/lib/queryClient";
import { pollFloorplanJob } from "@/lib/floorplanUpload";
import { uploadFileDirect, classifyDirectUpload } from "@/lib/directUpload";
import { computeTrueAprFromRows } from "@/lib/realApr";

// ─── Types ─────────────────────────────────────────────────────────────────────

interface Receivable {
  vin?: string;
  stock?: string;
  principal?: number;
  originalAmount?: number;
  daysOutstanding?: number;
  interest?: number;
  fees?: number;
}

interface ParsedResult {
  statement: any;
  parsed: {
    lenderName: string;
    lenderKey: string;
    statementPeriod: { start?: string; end?: string };
    totals: {
      totalInterestPaid: number;
      totalFeesPaid: number;
      averageBalance: number;
      unitsSold: number;
      periodDays: number;
      avgDaysInStock: number;
    };
    feeBreakdown: {
      feeAdmin: number;
      feeAudit: number;
      feeCurtailment: number;
      feeDoc: number;
      feeTitle: number;
      feeOther: number;
    };
    receivables: Receivable[];
    detectedFieldsConfidence: Record<string, number>;
    warnings: string[];
  };
  extractionMethod: string;
  lenderTerms?: Record<string, any> | null;
  difficulty?: string | null;
  termsApplied?: boolean;
  success: boolean;
}

interface ReviewForm {
  lenderName: string;
  statementPeriodStart: string;
  statementPeriodEnd: string;
  statementMonth: string;
  statementYear: string;
  totalInterestPaid: string;
  totalFeesPaid: string;
  averageBalance: string;
  unitsSold: string;
  periodDays: string;
  avgDaysInStock: string;
  /** True APR computed from uncurtailed vehicles — pre-filled automatically, user-editable */
  aprRate: string;
  feeAdmin: string;
  feeAudit: string;
  feeCurtailment: string;
  feeDoc: string;
  feeTitle: string;
  feeOther: string;
  notes: string;
  receivablesJson?: string;
}

interface EditableReceivable {
  id: number;
  vin: string;
  stock: string;
  principal: string;
  originalAmount: string;
  daysOutstanding: string;
  interest: string;
  fees: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt$ = (n: number | null | undefined, d = 0) =>
  n == null || isNaN(n) ? "—" : `$${n.toLocaleString("en-US", { maximumFractionDigits: d })}`;
const fmtPct = (n: number | null | undefined) =>
  n == null || isNaN(n) ? "—" : `${n.toFixed(2)}%`;

const CONFIDENCE_COLOR = (c: number) =>
  c >= 0.8 ? "text-chart-3" : c >= 0.5 ? "text-yellow-500" : "text-destructive";

const PIE_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
  "#a78bfa",
];

function inferMonthYearFromPeriod(start?: string, end?: string): { month: string; year: string } {
  const dateStr = end || start;
  if (!dateStr) return { month: "", year: "" };
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return { month: "", year: "" };
  return { month: String(d.getMonth() + 1), year: String(d.getFullYear()) };
}

function parsedToReviewForm(parsed: ParsedResult["parsed"], statement?: any): ReviewForm {
  const inferred = inferMonthYearFromPeriod(parsed.statementPeriod.start, parsed.statementPeriod.end);
  const storedMonth = statement?.statementMonth ? String(statement.statementMonth) : "";
  const storedYear = statement?.statementYear ? String(statement.statementYear) : "";
  return {
    lenderName: parsed.lenderName ?? "",
    statementPeriodStart: parsed.statementPeriod.start ?? "",
    statementPeriodEnd: parsed.statementPeriod.end ?? "",
    statementMonth: storedMonth || inferred.month,
    statementYear: storedYear || inferred.year,
    totalInterestPaid: String(parsed.totals.totalInterestPaid || ""),
    totalFeesPaid: String(parsed.totals.totalFeesPaid || ""),
    averageBalance: String(parsed.totals.averageBalance || ""),
    unitsSold: String(parsed.totals.unitsSold || ""),
    periodDays: String(parsed.totals.periodDays || "30"),
    avgDaysInStock: String(parsed.totals.avgDaysInStock || ""),
    aprRate: "", // Will be auto-computed from receivable rows via useEffect
    feeAdmin: String(parsed.feeBreakdown.feeAdmin || ""),
    feeAudit: String(parsed.feeBreakdown.feeAudit || ""),
    feeCurtailment: String(parsed.feeBreakdown.feeCurtailment || ""),
    feeDoc: String(parsed.feeBreakdown.feeDoc || ""),
    feeTitle: String(parsed.feeBreakdown.feeTitle || ""),
    feeOther: String(parsed.feeBreakdown.feeOther || ""),
    notes: "",
    receivablesJson: JSON.stringify(parsed.receivables),
  };
}

function reviewFormToEngineEntry(form: ReviewForm) {
  return {
    totalInterestPaid: form.totalInterestPaid,
    totalFeesPaid: form.totalFeesPaid,
    avgInventoryBalance: form.averageBalance,
    unitsSold: form.unitsSold,
    periodDays: form.periodDays,
    avgDaysOnFloor: form.avgDaysInStock,
    floorplanFee: form.feeAdmin,
    auditFee: form.feeAudit,
    curtailmentFee: form.feeCurtailment,
    docFee: form.feeDoc,
    titleFee: form.feeTitle,
    ancillaryFee1: form.feeOther,
    statedRate: "0",
    avgGrossProfitPerSale: "",
    avgPurchasePrice: "",
    dealerWacc: "",
    inventoryUnitsAvg: "",
    unusedLineAmountAvg: "",
    curtailmentPaidPrincipal: "",
    ancillaryFee2: "", ancillaryFee3: "", ancillaryFee4: "",
  };
}

// ─── Upload Dropzone ──────────────────────────────────────────────────────────

function analysisSteps(t: TFunction): string[] {
  return Array.from({ length: 17 }, (_, i) => t(`floorplan:reader.steps.s${i + 1}`));
}

// Quick client-side size estimate shown immediately while the server pre-flight call is in-flight.
// label is a translation key resolved at render time.
function quickSizeEstimate(file: File): { label: string; eta: string } {
  const ext = "." + file.name.split(".").pop()!.toLowerCase();
  const mb = file.size / 1_048_576;
  if (ext !== ".pdf") return { label: "floorplan:reader.size.imageAnalysis", eta: "~20s" };
  if (mb > 3) return { label: "floorplan:reader.size.scanned", eta: "~60s" };
  if (mb > 0.5) return { label: "floorplan:reader.size.mixedPdf", eta: "~30s" };
  return { label: "floorplan:reader.size.cleanPdf", eta: "~15s" };
}

// Convert server-provided estimatedSeconds to a short ETA label matching spec format.
function secondsToEta(sec: number): string {
  if (sec <= 20) return "~15s";
  if (sec <= 35) return "~30s";
  return "~60s";
}

function UploadDropzone({
  onUploadComplete,
  onUploadStart,
  onMinimize,
}: {
  onUploadComplete: (result: ParsedResult) => void;
  onUploadStart?: () => void;
  onMinimize?: () => void;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [fileEstimate, setFileEstimate] = useState<{ label: string; eta: string } | null>(null);
  const [streamingLines, setStreamingLines] = useState<string[]>([]);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const minimizedRef = useRef(false);
  const canceledRef = useRef(false);
  const activeJobIdRef = useRef<string | null>(null);
  const { toast } = useToast();
  const { t } = useTranslation(["floorplan", "common"]);
  const scanner = useScannerContext();
  const scannerRef = useRef(scanner);
  scannerRef.current = scanner;

  // Cancel-on-back: if this dropzone unmounts (dialog closed / user navigated
  // back) while a scan is still running and the user did NOT explicitly
  // minimize, cancel the server-side job instead of letting it finish silently.
  useEffect(() => {
    return () => {
      const jobId = activeJobIdRef.current;
      if (jobId && !minimizedRef.current && !canceledRef.current) {
        canceledRef.current = true;
        fetch(`/api/parse-floorplan/cancel/${jobId}`, { method: "POST", credentials: "include" }).catch(() => {});
        scannerRef.current.clear();
      }
    };
  }, []);

  useEffect(() => {
    if (isUploading) {
      setStreamingLines([]);
      let idx = 0;
      const steps = analysisSteps(t);
      timerRef.current = setInterval(() => {
        setStreamingLines((prev) => {
          const next = idx < steps.length
            ? [...prev, steps[idx]]
            : [...prev, t("floorplan:reader.stillWorking")];
          idx++;
          return next;
        });
      }, 900);
    } else {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setStreamingLines([]);
    }
    return () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  }, [isUploading]);

  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [streamingLines]);

  const handleFile = useCallback(async (file: File) => {
    const allowed = [".pdf", ".jpg", ".jpeg", ".png"];
    const ext = "." + file.name.split(".").pop()!.toLowerCase();
    if (!allowed.includes(ext)) {
      toast({ title: t("floorplan:reader.invalidFileType"), description: t("floorplan:reader.invalidFileTypeDesc"), variant: "destructive" });
      return;
    }

    // Reset minimize flag for this new scan session.
    minimizedRef.current = false;

    // Notify parent immediately so any prior error UI can be cleared.
    onUploadStart?.();

    // Show a quick client-side estimate immediately while the server pre-flight runs.
    setFileEstimate(quickSizeEstimate(file));
    setIsUploading(true);
    setUploadProgress(ext === ".pdf" ? t("floorplan:reader.analyzingFinancials") : t("floorplan:reader.analyzingImage"));

    try {
      // Step 1: direct-to-storage upload — presign, then PUT the bytes
      // straight to object storage. Express never sees the file body.
      const { uploadId } = await uploadFileDirect(file, "floorplan");

      // Step 2: pre-flight classify on the stored object — server extracts
      // text, counts chars + pages, and returns the difficulty classification.
      if (ext === ".pdf") {
        const cls = await classifyDirectUpload(uploadId);
        if (cls?.label && cls?.estimatedSeconds != null) {
          setFileEstimate({ label: cls.label, eta: secondsToEta(cls.estimatedSeconds) });
        }
      }

      // Step 3: finalize — server validates the stored object and returns 202 + jobId
      const response = await fetch("/api/parse-floorplan/finalize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uploadId }),
        credentials: "include",
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || t("floorplan:reader.uploadFailed"));
      }

      const responseJson = await response.json();

      // Dedup short-circuit: server returned an already-processed statement directly.
      if (responseJson.deduplicated && responseJson.statement && !responseJson.jobId) {
        const stmt = responseJson.statement;
        const dedupResult: ParsedResult = {
          statement: stmt,
          parsed: {
            lenderName: stmt.lenderName ?? "",
            lenderKey: stmt.lenderKey ?? "generic",
            statementPeriod: { start: stmt.statementPeriodStart, end: stmt.statementPeriodEnd },
            totals: {
              totalInterestPaid: parseFloat(stmt.totalInterestPaid || "0"),
              totalFeesPaid: parseFloat(stmt.totalFeesPaid || "0"),
              averageBalance: parseFloat(stmt.averageBalance || "0"),
              unitsSold: stmt.unitsSold ?? 0,
              periodDays: stmt.periodDays ?? 30,
              avgDaysInStock: parseFloat(stmt.avgDaysInStock || "0"),
            },
            feeBreakdown: {
              feeAdmin: parseFloat(stmt.feeAdmin || "0"),
              feeAudit: parseFloat(stmt.feeAudit || "0"),
              feeCurtailment: parseFloat(stmt.feeCurtailment || "0"),
              feeDoc: parseFloat(stmt.feeDoc || "0"),
              feeTitle: parseFloat(stmt.feeTitle || "0"),
              feeOther: parseFloat(stmt.feeOther || "0"),
            },
            receivables: JSON.parse(stmt.receivablesJson || "[]"),
            detectedFieldsConfidence: JSON.parse(stmt.confidenceJson || "{}"),
            warnings: JSON.parse(stmt.warningsJson || "[]"),
          },
          extractionMethod: stmt.extractionMethod ?? "cached",
          success: true,
        };
        onUploadComplete(dedupResult);
        scanner.clear();
        return;
      }

      const { jobId } = responseJson;

      // Register with background scanner context (enables minimize-and-continue)
      setActiveJobId(jobId);
      activeJobIdRef.current = jobId;
      canceledRef.current = false;
      scanner.startScan(jobId, window.location.pathname);

      // Step 3: poll until done or error.
      // When the user minimizes the modal, minimizedRef is set to true and this loop
      // returns null — handing off tracking to the global ScannerContext (single authority).
      setUploadProgress(ext === ".pdf" ? t("floorplan:reader.extractingFinancials") : t("floorplan:reader.analyzingImage"));
      const result: ParsedResult | null = await (async () => {
        const POLL_INTERVAL = 2000;
        // Inactivity-based deadline (not an absolute wall): give up only after the
        // server has reported NO progress for this long. The worker emits a liveness
        // heartbeat every ~20s during the long AI step, so a healthy big receivable —
        // however long it takes — keeps resetting this deadline and is never abandoned.
        // A genuinely dead worker stops updating and trips it after the window.
        const INACTIVITY_TIMEOUT = 8 * 60 * 1000;
        let lastProgressToken: string | null = null;
        let lastProgressAt = Date.now();
        while (true) {
          // Check before sleep so minimize/cancel takes effect immediately.
          if (minimizedRef.current || canceledRef.current) return null;
          await new Promise((r) => setTimeout(r, POLL_INTERVAL));
          // Check again after sleep in case minimize/cancel happened during the wait.
          if (minimizedRef.current || canceledRef.current) return null;
          const statusRes = await fetch(`/api/parse-floorplan/status/${jobId}`, { credentials: "include" });
          const statusJson = await statusRes.json();
          if (canceledRef.current || statusJson.status === "canceled") return null;
          if (statusJson.status === "done") return statusJson.result as ParsedResult;
          if (statusJson.status === "error") throw new Error(statusJson.error || t("floorplan:reader.extractionFailedError"));
          // Reset the inactivity deadline whenever the server's progress advances.
          const token = `${statusJson.step ?? ""}|${statusJson.pct ?? ""}`;
          if (token !== lastProgressToken) {
            lastProgressToken = token;
            lastProgressAt = Date.now();
          } else if (Date.now() - lastProgressAt > INACTIVITY_TIMEOUT) {
            if (minimizedRef.current) return null;
            throw new Error(t("floorplan:reader.extractionStalled"));
          }
          if (statusJson.status === "queued") {
            setUploadProgress(t("floorplan:reader.queued"));
          } else if (statusJson.status === "processing") {
            // Surface the server's live step (incl. the heartbeat's elapsed-seconds
            // marker) so a long-running big receivable visibly proves it's working.
            setUploadProgress(statusJson.step || (ext === ".pdf" ? t("floorplan:reader.extractingFinancials") : t("floorplan:reader.analyzingImage")));
          }
        }
      })();

      if (result !== null && !canceledRef.current) {
        // Completed in-modal — deliver result locally and clear context.
        onUploadComplete(result);
        scanner.clear();
      }
      // result === null means the user minimized (ScannerContext is the sole
      // authority now) or canceled (job already told to stop server-side).
    } catch (err: any) {
      if (!minimizedRef.current && !canceledRef.current) {
        // Only show error toast and clear context when not minimized.
        // If minimized, context polling will surface its own error state.
        toast({ title: t("floorplan:reader.uploadFailed"), description: err.message, variant: "destructive", duration: Infinity });
        scanner.clear();
      }
    } finally {
      setIsUploading(false);
      setUploadProgress("");
      setFileEstimate(null);
      setActiveJobId(null);
      activeJobIdRef.current = null;
    }
  }, [toast, onUploadComplete, onUploadStart, scanner, t]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  return (
    <div className="space-y-6">
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => !isUploading && fileRef.current?.click()}
        className={`
          relative border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all
          ${isDragging ? "border-primary bg-primary/5 scale-[1.01]" : "border-border hover:border-primary/50 hover:bg-muted/30"}
          ${isUploading ? "pointer-events-none opacity-70" : ""}
        `}
        data-testid="upload-dropzone"
      >
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
        />
        {isUploading ? (
          <div className="space-y-4 text-left" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 justify-center">
              <RefreshCw className="w-5 h-5 text-primary animate-spin shrink-0" />
              <p className="text-sm font-semibold text-primary">{uploadProgress}</p>
              {activeJobId && onMinimize && (
                <Button
                  size="sm"
                  variant="outline"
                  className="ml-2 text-xs h-7 px-2 shrink-0"
                  onClick={(e) => { e.stopPropagation(); minimizedRef.current = true; scanner.minimize(); onMinimize?.(); }}
                  data-testid="button-minimize-scanner"
                >
                  {t("floorplan:reader.minimize")}
                </Button>
              )}
            </div>
            {fileEstimate && (
              <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                <span className="font-medium text-foreground/70">{t(fileEstimate.label)}</span>
                <span>·</span>
                <span>{t("floorplan:reader.est", { eta: fileEstimate.eta })}</span>
              </div>
            )}
            <div className="rounded-lg border bg-muted/40 overflow-hidden">
              <div className="px-3 py-1.5 border-b bg-muted/60 flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-red-400" />
                <div className="w-2 h-2 rounded-full bg-yellow-400" />
                <div className="w-2 h-2 rounded-full bg-green-400" />
                <span className="text-[10px] text-muted-foreground ml-1 font-mono">{t("floorplan:reader.analysisLog")}</span>
              </div>
              <div
                ref={streamRef}
                className="h-32 overflow-y-auto px-3 py-2 font-mono text-[11px] space-y-0.5"
              >
                {streamingLines.map((line, i) => (
                  <div key={i} className={`flex items-start gap-1.5 ${i === streamingLines.length - 1 ? "text-primary" : "text-muted-foreground"}`}>
                    <span className="text-green-500 shrink-0 select-none">{i === streamingLines.length - 1 ? "▶" : "✓"}</span>
                    <span>{line}</span>
                  </div>
                ))}
                {streamingLines.length === 0 && (
                  <span className="text-muted-foreground">{t("floorplan:reader.startingAnalysis")}</span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex justify-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Image className="w-6 h-6 text-primary" />
              </div>
            </div>
            <div>
              <p className="text-base font-semibold">{t("floorplan:reader.dropHere")}</p>
              <p className="text-sm text-muted-foreground mt-1">{t("floorplan:reader.clickToBrowse")}</p>
            </div>
            <div className="flex justify-center gap-2 flex-wrap">
              {["PDF", "JPG", "PNG"].map((t) => (
                <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("floorplan:reader.pdfHint")}
            </p>
          </div>
        )}
      </div>

      {/* Supported lenders info */}
      <div className="p-3 rounded-lg bg-muted/40 text-xs text-muted-foreground">
        <p className="font-medium mb-1">{t("floorplan:reader.supportedFormats")}</p>
        <div className="flex flex-wrap gap-2 mt-1">
          {["NextGear Capital", "AFC", "Ally Financial", "Manheim", "Floorplan Xpress"].map((l) => (
            <Badge key={l} variant="outline" className="text-xs">{l}</Badge>
          ))}
          <Badge variant="outline" className="text-xs">{t("floorplan:reader.genericFallback")}</Badge>
        </div>
      </div>
    </div>
  );
}

// ─── Editable Receivables Section ────────────────────────────────────────────

function rowAnomalies(r: EditableReceivable): { noVin: boolean; zeroDaysInterest: boolean; interestOverPrincipal: boolean } {
  const vin = r.vin.trim();
  const days = parseInt(r.daysOutstanding) || 0;
  const interest = parseFloat(r.interest) || 0;
  const principal = parseFloat(r.principal) || 0;
  return {
    noVin: !vin || vin.length < 5,
    zeroDaysInterest: days === 0 && interest > 0,
    interestOverPrincipal: principal > 0 && interest > principal,
  };
}

function rowIsFlagged(r: EditableReceivable): boolean {
  const a = rowAnomalies(r);
  return a.noVin || a.zeroDaysInterest || a.interestOverPrincipal;
}

function rowAnomalyLabel(r: EditableReceivable, t: TFunction): string {
  const a = rowAnomalies(r);
  const parts: string[] = [];
  if (a.noVin) parts.push(t("floorplan:reader.anomaly.noVin"));
  if (a.zeroDaysInterest) parts.push(t("floorplan:reader.anomaly.zeroDays"));
  if (a.interestOverPrincipal) parts.push(t("floorplan:reader.anomaly.interestOverPrincipal"));
  return parts.join("; ");
}

function matchQuality(sum: number, doc: number | undefined): "green" | "yellow" | "red" | "none" {
  if (!doc || doc <= 0) return "none";
  const pct = Math.abs(sum - doc) / doc * 100;
  if (pct <= 1) return "green";
  if (pct <= 5) return "yellow";
  return "red";
}

function EditableReceivablesSection({
  rows,
  accuracyStatus,
  docInterest,
  docFees,
  showReceivables,
  onToggle,
  onEditRow,
  onDeleteRow,
  onAddRow,
}: {
  rows: EditableReceivable[];
  accuracyStatus: string;
  docInterest?: number;
  docFees?: number;
  showReceivables: boolean;
  onToggle: () => void;
  onEditRow: (id: number, field: keyof EditableReceivable, value: string) => void;
  onDeleteRow: (id: number) => void;
  onAddRow: () => void;
}) {
  const { t } = useTranslation(["floorplan", "common"]);
  const interestSum = rows.reduce((s, r) => s + (parseFloat(r.interest) || 0), 0);
  const feesSum = rows.reduce((s, r) => s + (parseFloat(r.fees) || 0), 0);
  const flaggedCount = rows.filter(rowIsFlagged).length;

  const iQ = matchQuality(interestSum, docInterest);
  const fQ = matchQuality(feesSum, docFees);

  const inputCls = "h-6 text-xs px-1 py-0 border-0 bg-transparent focus:bg-background focus:border-border focus:ring-0 focus:outline-none rounded";

  return (
    <div>
      {/* Section header */}
      <button
        onClick={onToggle}
        className="flex items-center gap-2 text-sm font-semibold w-full text-left mb-1"
        data-testid="toggle-receivables"
      >
        {showReceivables ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        {t("floorplan:reader.receivableLineItems", { count: rows.length })}
        {flaggedCount > 0 && (
          <span className="ml-1 flex items-center gap-0.5 text-xs font-normal text-yellow-600">
            <AlertTriangle className="w-3 h-3" />
            {t("floorplan:reader.flagged", { count: flaggedCount })}
          </span>
        )}
      </button>

      {showReceivables && (
        <div className="space-y-2">
          {/* Validation summary bar */}
          <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 rounded-md border text-xs ${
            flaggedCount > 0 || iQ === "red" || fQ === "red" ? "bg-yellow-50 border-yellow-200 dark:bg-yellow-950/20 dark:border-yellow-800"
            : "bg-muted/30 border-border"
          }`} data-testid="receivables-summary-bar">
            <span className="font-medium">{t("floorplan:reader.vehicleCount", { count: rows.length })}</span>
            <span>·</span>
            <span>
              <span className="text-muted-foreground">{t("floorplan:reader.interestLabel")}</span>
              <span className={`font-medium ${iQ === "green" ? "text-green-700" : iQ === "red" ? "text-red-600" : iQ === "yellow" ? "text-yellow-700" : ""}`}>
                {fmt$(interestSum, 2)}
              </span>
              {docInterest && docInterest > 0 && (
                <span className="text-muted-foreground ml-1">{t("floorplan:reader.docValue", { value: fmt$(docInterest, 2) })}</span>
              )}
            </span>
            <span>·</span>
            <span>
              <span className="text-muted-foreground">{t("floorplan:reader.feesLabel")}</span>
              <span className={`font-medium ${fQ === "green" ? "text-green-700" : fQ === "red" ? "text-red-600" : fQ === "yellow" ? "text-yellow-700" : ""}`}>
                {fmt$(feesSum, 2)}
              </span>
              {docFees && docFees > 0 && (
                <span className="text-muted-foreground ml-1">{t("floorplan:reader.docValue", { value: fmt$(docFees, 2) })}</span>
              )}
            </span>
            {accuracyStatus === "needs-review" && (
              <span className="flex items-center gap-0.5 text-yellow-600">
                <ShieldAlert className="w-3 h-3" />
                {t("floorplan:reader.aiCouldNotReconcile")}
              </span>
            )}
          </div>

          {/* Editable table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b bg-muted/20">
                  <th className="text-left py-1.5 px-2 text-muted-foreground font-medium w-40">{t("floorplan:reader.th.vin")}</th>
                  <th className="text-left py-1.5 px-2 text-muted-foreground font-medium w-24">{t("floorplan:reader.th.stock")}</th>
                  <th className="text-right py-1.5 px-2 text-muted-foreground font-medium w-24">{t("floorplan:reader.th.principal")}</th>
                  <th className="text-right py-1.5 px-2 text-muted-foreground font-medium w-24">
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-default">{t("floorplan:reader.th.origAmt")}</span>
                        </TooltipTrigger>
                        <TooltipContent className="text-xs max-w-[200px]">{t("floorplan:reader.th.origAmtTooltip")}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </th>
                  <th className="text-right py-1.5 px-2 text-muted-foreground font-medium w-16">{t("floorplan:reader.th.days")}</th>
                  <th className="text-right py-1.5 px-2 text-muted-foreground font-medium w-24">{t("floorplan:reader.th.interest")}</th>
                  <th className="text-right py-1.5 px-2 text-muted-foreground font-medium w-24">{t("floorplan:reader.th.fees")}</th>
                  <th className="w-7"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const flagged = rowIsFlagged(r);
                  const label = flagged ? rowAnomalyLabel(r, t) : "";
                  return (
                    <tr
                      key={r.id}
                      className={`border-b last:border-0 group hover:bg-muted/10 ${flagged ? "border-l-2 border-l-yellow-400" : ""}`}
                      data-testid={`receivable-row-${r.id}`}
                    >
                      <td className="py-0.5 px-1">
                        <div className="flex items-center gap-0.5">
                          {flagged && (
                            <TooltipProvider delayDuration={0}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <AlertTriangle className="w-3 h-3 text-yellow-500 shrink-0 cursor-default" />
                                </TooltipTrigger>
                                <TooltipContent side="right" className="max-w-[200px] text-xs">
                                  {label}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          <input
                            className={`${inputCls} font-mono w-full`}
                            value={r.vin}
                            onChange={e => onEditRow(r.id, "vin", e.target.value)}
                            placeholder={t("floorplan:reader.vinPlaceholder")}
                            data-testid={`cell-vin-${r.id}`}
                          />
                        </div>
                      </td>
                      <td className="py-0.5 px-1">
                        <input
                          className={`${inputCls} w-full`}
                          value={r.stock}
                          onChange={e => onEditRow(r.id, "stock", e.target.value)}
                          placeholder="—"
                          data-testid={`cell-stock-${r.id}`}
                        />
                      </td>
                      <td className="py-0.5 px-1">
                        <input
                          className={`${inputCls} w-full text-right`}
                          type="number"
                          step="any"
                          value={r.principal}
                          onChange={e => onEditRow(r.id, "principal", e.target.value)}
                          placeholder="0"
                          data-testid={`cell-principal-${r.id}`}
                        />
                      </td>
                      <td className="py-0.5 px-1">
                        <input
                          className={`${inputCls} w-full text-right ${
                            r.originalAmount && r.principal
                              ? Math.abs((parseFloat(r.principal) || 0) - (parseFloat(r.originalAmount) || 0)) <= 1
                                ? "text-chart-3"
                                : "text-muted-foreground/60"
                              : ""
                          }`}
                          type="number"
                          step="any"
                          value={r.originalAmount}
                          onChange={e => onEditRow(r.id, "originalAmount", e.target.value)}
                          placeholder="—"
                          data-testid={`cell-originalAmount-${r.id}`}
                        />
                      </td>
                      <td className="py-0.5 px-1">
                        <input
                          className={`${inputCls} w-full text-right ${(parseInt(r.daysOutstanding) || 0) === 0 && (parseFloat(r.interest) || 0) > 0 ? "text-yellow-600" : ""}`}
                          type="number"
                          step="1"
                          value={r.daysOutstanding}
                          onChange={e => onEditRow(r.id, "daysOutstanding", e.target.value)}
                          placeholder="0"
                          data-testid={`cell-days-${r.id}`}
                        />
                      </td>
                      <td className="py-0.5 px-1">
                        <input
                          className={`${inputCls} w-full text-right`}
                          type="number"
                          step="any"
                          value={r.interest}
                          onChange={e => onEditRow(r.id, "interest", e.target.value)}
                          placeholder="0"
                          data-testid={`cell-interest-${r.id}`}
                        />
                      </td>
                      <td className="py-0.5 px-1">
                        <input
                          className={`${inputCls} w-full text-right`}
                          type="number"
                          step="any"
                          value={r.fees}
                          onChange={e => onEditRow(r.id, "fees", e.target.value)}
                          placeholder="0"
                          data-testid={`cell-fees-${r.id}`}
                        />
                      </td>
                      <td className="py-0.5 px-1">
                        <button
                          onClick={() => onDeleteRow(r.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive p-0.5 rounded"
                          title={t("floorplan:reader.removeRow")}
                          data-testid={`btn-delete-row-${r.id}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Add vehicle row */}
          <button
            onClick={onAddRow}
            className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors py-1 px-2"
            data-testid="btn-add-vehicle"
          >
            <Plus className="w-3.5 h-3.5" />
            {t("floorplan:reader.addVehicle")}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Review Panel ─────────────────────────────────────────────────────────────

function ReviewPanel({
  result,
  onSave,
  onSaveAsEntry,
}: {
  result: ParsedResult;
  onSave: (form: ReviewForm, statementId: string) => Promise<void>;
  onSaveAsEntry?: (form: ReviewForm) => void;
}) {
  const [form, setForm] = useState<ReviewForm>(parsedToReviewForm(result.parsed, result.statement));
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation(["floorplan", "common"]);

  // ─── Editable receivable rows state ─────────────────────────────────────────
  const [receivableRows, setReceivableRows] = useState<EditableReceivable[]>(() =>
    result.parsed.receivables.map((r, i) => ({
      id: i,
      vin: r.vin ?? "",
      stock: r.stock ?? "",
      principal: r.principal != null ? String(r.principal) : "",
      originalAmount: r.originalAmount != null ? String(r.originalAmount) : "",
      daysOutstanding: r.daysOutstanding != null ? String(r.daysOutstanding) : "",
      interest: r.interest != null ? String(r.interest) : "",
      fees: r.fees != null ? String(r.fees) : "",
    }))
  );
  const nextRowId = useRef(result.parsed.receivables.length);

  // Auto-sync form totals and receivablesJson from editable rows (including empty array)
  useEffect(() => {
    const interestSum = receivableRows.reduce((s, r) => s + (parseFloat(r.interest) || 0), 0);
    const feesSum = receivableRows.reduce((s, r) => s + (parseFloat(r.fees) || 0), 0);
    const serialized = JSON.stringify(
      receivableRows.map(r => ({
        vin: r.vin.trim() || null,
        stock: r.stock.trim() || null,
        principal: parseFloat(r.principal) || 0,
        originalAmount: parseFloat(r.originalAmount) || undefined,
        daysOutstanding: parseInt(r.daysOutstanding) || 0,
        interest: parseFloat(r.interest) || 0,
        fees: parseFloat(r.fees) || 0,
      }))
    );
    setForm(f => ({
      ...f,
      totalInterestPaid: interestSum.toFixed(2),
      totalFeesPaid: feesSum.toFixed(2),
      receivablesJson: serialized,
    }));
  }, [receivableRows]);

  // Auto-compute True APR from receivable rows and pre-fill form.aprRate
  const aprRateUserEdited = useRef(false);
  useEffect(() => {
    if (aprRateUserEdited.current) return;
    const trueApr = computeTrueAprFromRows(receivableRows.map(r => ({
      principal: parseFloat(r.principal) || 0,
      originalAmount: parseFloat(r.originalAmount) || 0,
      daysOutstanding: parseInt(r.daysOutstanding) || 0,
      interest: parseFloat(r.interest) || 0,
      fees: parseFloat(r.fees) || 0,
    })));
    if (!trueApr.insufficientData && !trueApr.noInterestAccrued && !trueApr.isEstimate && trueApr.apr != null) {
      setForm(f => ({ ...f, aprRate: trueApr.apr!.toFixed(4) }));
    } else {
      setForm(f => ({ ...f, aprRate: "" }));
    }
  }, [receivableRows]);

  const editRow = (id: number, field: keyof EditableReceivable, value: string) =>
    setReceivableRows(rows => rows.map(r => r.id === id ? { ...r, [field]: value } : r));
  const deleteRow = (id: number) =>
    setReceivableRows(rows => rows.filter(r => r.id !== id));
  const addRow = () => {
    const newId = nextRowId.current++;
    setReceivableRows(rows => [...rows, { id: newId, vin: "", stock: "", principal: "", originalAmount: "", daysOutstanding: "", interest: "", fees: "" }]);
  };

  // ─── Accuracy check info ─────────────────────────────────────────────────────
  const accuracyInfo = (() => {
    try { return JSON.parse(result.statement?.accuracyCheckJson ?? "{}") as { status?: string; interestDocTotal?: number; feesDocTotal?: number; }; }
    catch { return {}; }
  })();
  const accuracyStatus = accuracyInfo.status ?? "unverified";

  // Auto-expand when anomalies are present.
  // Row anomaly rules (intentional distinction):
  //   - days=0 AND interest>0  → anomaly: AI misread the days column (should have been > 0)
  //   - days=0 AND interest=0  → NOT an anomaly: car was legitimately floored on the statement
  //                              date; zero days with zero cost is a perfectly valid state.
  // The floor-date fallback in the extraction pipeline resolves most days=0+interest>0 cases
  // before the UI receives the data, so flagged rows here require manual review.
  const [showReceivables, setShowReceivables] = useState(
    accuracyStatus === "needs-review" || receivableRows.some(r => {
      const vin = r.vin.trim(); const days = parseInt(r.daysOutstanding) || 0;
      const interest = parseFloat(r.interest) || 0; const principal = parseFloat(r.principal) || 0;
      return !vin || vin.length < 5 || (days === 0 && interest > 0) || (principal > 0 && interest > principal);
    })
  );

  const handleChange = (k: keyof ReviewForm, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const engineEntry = reviewFormToEngineEntry(form);
  const liveMetrics = computeAllMetrics(engineEntry);
  const bd = liveMetrics.hiddenCostBreakdown;

  const extractionBadge = ({
    "pdf-text": { label: t("floorplan:reader.extraction.pdfText"), color: "bg-chart-3/10 text-chart-3" },
    "ai-pdf": { label: t("floorplan:reader.extraction.aiPdf"), color: "bg-chart-1/10 text-chart-1" },
    "ai-image": { label: t("floorplan:reader.extraction.aiImage"), color: "bg-chart-1/10 text-chart-1" },
    "ocr": { label: t("floorplan:reader.extraction.ocr"), color: "bg-yellow-500/10 text-yellow-600" },
    "manual-required": { label: t("floorplan:reader.extraction.manualRequired"), color: "bg-destructive/10 text-destructive" },
  } as Record<string, { label: string; color: string }>)[result.extractionMethod] ?? { label: result.extractionMethod ?? t("floorplan:reader.extraction.unknown"), color: "bg-muted/40 text-muted-foreground" };

  const warnings = result.parsed.warnings ?? [];
  const confidence = result.parsed.detectedFieldsConfidence ?? {};

  const lowConfidenceEvents: { step: string; detail?: string }[] = (() => {
    try {
      const diag = JSON.parse(result.statement?.extractionDiagnosticsJson ?? "{}");
      const events: { step: string; outcome: string; detail?: string }[] = diag.pipelineEvents ?? [];
      return events.filter((e) => e.outcome === "low-confidence");
    } catch {
      return [];
    }
  })();

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form, result.statement.id);
      toast({ title: t("floorplan:reader.statementSavedTitle"), description: t("floorplan:reader.statementSavedDesc") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Lender Name — editable right at the top */}
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Edit3 className="w-3 h-3" />{t("floorplan:reader.lenderName")}
        </Label>
        <Input
          value={form.lenderName}
          onChange={(e) => handleChange("lenderName", e.target.value)}
          placeholder={t("floorplan:reader.lenderPlaceholder")}
          className="text-base font-semibold h-10"
          data-testid="input-review-lender"
        />
        {form.lenderName !== result.parsed.lenderName && (
          <p className="text-xs text-muted-foreground">
            {t("floorplan:reader.originallyDetected")} <span className="italic">{result.parsed.lenderName || t("floorplan:reader.unknownLender")}</span>
          </p>
        )}
      </div>

      {/* Extraction method + warnings */}
      <div className="flex items-start gap-3 flex-wrap">
        <Badge className={`text-xs ${extractionBadge.color}`} variant="outline">
          {t("floorplan:reader.extractionLabel", { method: extractionBadge.label })}
        </Badge>
        {!result.success && (
          <Badge className="text-xs bg-destructive/10 text-destructive" variant="outline">
            {t("floorplan:reader.extractionHadErrors")}
          </Badge>
        )}
      </div>

      {warnings.length > 0 && (
        <div className="space-y-1.5">
          {warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-xs text-yellow-700 dark:text-yellow-400">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {lowConfidenceEvents.length > 0 && (
        <div
          className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40 px-4 py-3"
          data-testid="banner-low-confidence-warning"
        >
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              {t("floorplan:reader.someMissing")}
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-400">
              {t("floorplan:reader.someMissingDesc")}
            </p>
          </div>
        </div>
      )}

      {/* Live KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: t("floorplan:reader.kpi.trueMonthlyCost"), value: fmt$(liveMetrics.trueMonthlyCost.value) },
          { label: t("floorplan:reader.kpi.effectiveYield"), value: fmtPct(liveMetrics.effectiveYield.pct) },
          { label: t("floorplan:reader.kpi.costPerUnit"), value: fmt$(liveMetrics.perUnitCost.preferred) },
          { label: t("floorplan:reader.kpi.projectedAnnual"), value: fmt$(liveMetrics.projectedAnnualCost) },
        ].map((k) => (
          <div key={k.label} className="p-3 rounded-lg bg-primary/5 border border-primary/20">
            <p className="text-xs text-muted-foreground">{k.label}</p>
            <p className="text-lg font-bold text-primary mt-0.5">{k.value}</p>
          </div>
        ))}
      </div>

      <Separator />

      {/* Editable review form */}
      <div>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Edit3 className="w-4 h-4 text-primary" />{t("floorplan:reader.reviewEdit")}
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            { key: "statementPeriodStart", label: t("floorplan:reader.periodStart") },
            { key: "statementPeriodEnd", label: t("floorplan:reader.periodEnd") },
            { key: "periodDays", label: t("floorplan:reader.periodDays") },
          ].map(({ key, label }) => (
            <div key={key} className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs">{label}</Label>
                {confidence[key] != null && (
                  <span className={`text-xs ${CONFIDENCE_COLOR(confidence[key])}`}>
                    {t("floorplan:reader.confidence", { pct: Math.round(confidence[key] * 100) })}
                  </span>
                )}
              </div>
              <Input
                type="text"
                value={(form as any)[key]}
                onChange={(e) => handleChange(key as any, e.target.value)}
                data-testid={`input-review-${key}`}
              />
            </div>
          ))}
        </div>

        {/* Month / Year Tag */}
        <div className="mt-3 p-3 rounded-lg border border-primary/20 bg-primary/5 space-y-2">
          <div className="flex items-center gap-1.5">
            <Label className="text-xs font-semibold text-primary">{t("floorplan:reader.statementMonthYear")}</Label>
            <span className="text-xs text-muted-foreground">{t("floorplan:reader.monthTrackingHint")}</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t("floorplan:reader.month")}</Label>
              <Select
                value={form.statementMonth}
                onValueChange={(v) => handleChange("statementMonth", v)}
              >
                <SelectTrigger data-testid="select-statement-month" className="h-8 text-xs">
                  <SelectValue placeholder={t("floorplan:reader.selectMonth")} />
                </SelectTrigger>
                <SelectContent>
                  {["m1","m2","m3","m4","m5","m6","m7","m8","m9","m10","m11","m12"].map((m, i) => (
                    <SelectItem key={i+1} value={String(i+1)}>{t(`floorplan:reader.months.${m}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t("floorplan:reader.year")}</Label>
              <Input
                type="number"
                placeholder={t("floorplan:reader.yearPlaceholder")}
                value={form.statementYear}
                onChange={(e) => handleChange("statementYear", e.target.value)}
                className="h-8 text-xs"
                data-testid="input-statement-year"
              />
            </div>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            { key: "totalInterestPaid", label: t("floorplan:reader.totalInterestPaid"), prefix: "$" },
            { key: "averageBalance", label: t("floorplan:reader.avgBalance"), prefix: "$" },
            { key: "totalFeesPaid", label: t("floorplan:reader.totalFeesPaid"), prefix: "$" },
            { key: "unitsSold", label: t("floorplan:reader.unitsSold") },
            { key: "avgDaysInStock", label: t("floorplan:reader.avgDaysInStock") },
          ].map(({ key, label, prefix }) => (
            <div key={key} className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs">{label}</Label>
                {confidence[key] != null && (
                  <span className={`text-xs ${CONFIDENCE_COLOR(confidence[key])}`}>
                    {t("floorplan:reader.confidence", { pct: Math.round(confidence[key] * 100) })}
                  </span>
                )}
              </div>
              <div className="relative">
                {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">{prefix}</span>}
                <Input
                  type="number"
                  step="any"
                  value={(form as any)[key]}
                  onChange={(e) => handleChange(key as any, e.target.value)}
                  className={prefix ? "pl-6" : ""}
                  data-testid={`input-review-${key}`}
                />
              </div>
            </div>
          ))}

          {/* True APR — auto-computed from uncurtailed vehicles */}
          <div className="space-y-1.5 col-span-2 md:col-span-1">
            <div className="flex items-center justify-between">
              <Label className="text-xs">{t("floorplan:reader.trueAprLabel")}</Label>
              {form.aprRate && (
                <span className="text-[10px] text-primary font-medium">{t("floorplan:reader.autoCalculated")}</span>
              )}
            </div>
            <div className="relative">
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">%</span>
              <Input
                type="number"
                step="any"
                value={form.aprRate}
                onChange={(e) => {
                  aprRateUserEdited.current = true;
                  handleChange("aprRate", e.target.value);
                }}
                placeholder="—"
                className="pr-7"
                data-testid="input-review-aprRate"
              />
            </div>
            {(() => {
              const trueApr = computeTrueAprFromRows(receivableRows.map(r => ({
                principal: parseFloat(r.principal) || 0,
                originalAmount: parseFloat(r.originalAmount) || 0,
                daysOutstanding: parseInt(r.daysOutstanding) || 0,
                interest: parseFloat(r.interest) || 0,
                fees: parseFloat(r.fees) || 0,
              })));
              if (trueApr.isEstimate)
                return <p className="text-[10px] text-amber-600 dark:text-amber-400">{t("floorplan:reader.apr.estimated")}</p>;
              if (trueApr.isZeroInterest)
                return <p className="text-[10px] text-sky-600 dark:text-sky-400">{t("floorplan:reader.apr.zeroInterest")}</p>;
              if (trueApr.noInterestAccrued)
                return <p className="text-[10px] text-amber-600 dark:text-amber-400">{t("floorplan:reader.apr.noInterest")}</p>;
              if (trueApr.insufficientData)
                return <p className="text-[10px] text-amber-600 dark:text-amber-400">{t("floorplan:reader.apr.insufficient", { count: trueApr.usedCount })}</p>;
              if (trueApr.apr != null)
                return <p className="text-[10px] text-muted-foreground">{t("floorplan:reader.apr.calculated", { count: trueApr.usedCount })}{trueApr.excludedCount > 0 ? t("floorplan:reader.apr.excludedSuffix", { count: trueApr.excludedCount }) : ""}</p>;
              return null;
            })()}
          </div>
        </div>

        <div className="mt-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">{t("floorplan:reader.feeBreakdown")}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { key: "feeAdmin", label: t("floorplan:reader.fee.admin") },
              { key: "feeAudit", label: t("floorplan:reader.fee.audit") },
              { key: "feeCurtailment", label: t("floorplan:reader.fee.curtailment") },
              { key: "feeDoc", label: t("floorplan:reader.fee.doc") },
              { key: "feeTitle", label: t("floorplan:reader.fee.title") },
              { key: "feeOther", label: t("floorplan:reader.fee.other") },
            ].map(({ key, label }) => (
              <div key={key} className="space-y-1.5">
                <Label className="text-xs">{label}</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                  <Input
                    type="number"
                    step="any"
                    value={(form as any)[key]}
                    onChange={(e) => handleChange(key as any, e.target.value)}
                    className="pl-6"
                    data-testid={`input-review-${key}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-3 space-y-1.5">
          <Label className="text-xs">{t("floorplan:reader.notes")}</Label>
          <Input
            placeholder={t("floorplan:reader.notesPlaceholder")}
            value={form.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            data-testid="input-review-notes"
          />
        </div>
      </div>

      {/* Fee breakdown pie chart */}
      {bd.totalCost > 0 && bd.chartSlices.length > 0 && (
        <>
          <Separator />
          <div>
            <h3 className="text-sm font-semibold mb-3">{t("floorplan:reader.feeBreakdown")}</h3>
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="w-48 shrink-0">
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={bd.chartSlices} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={2} dataKey="value">
                      {bd.chartSlices.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(v: number) => [`${v.toFixed(1)}%`]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-1.5">
                {bd.chartSlices.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-muted-foreground">{s.name}</span>
                    </div>
                    <span className="font-medium">{s.value.toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* True APR Annotation — computed from receivable rows */}
      {(() => {
        const trueApr = computeTrueAprFromRows(receivableRows.map(r => ({
          principal: parseFloat(r.principal) || 0,
          originalAmount: parseFloat(r.originalAmount) || 0,
          daysOutstanding: parseInt(r.daysOutstanding) || 0,
          interest: parseFloat(r.interest) || 0,
          fees: parseFloat(r.fees) || 0,
        })));

        const hasData = receivableRows.some(r => parseFloat(r.principal) > 0);
        if (!hasData) return null;

        const isError = trueApr.insufficientData || trueApr.noInterestAccrued || (trueApr.isEstimate && !trueApr.isZeroInterest);

        return (
          <div className={`p-3 rounded-lg border text-sm ${
            isError
              ? "bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800"
              : "bg-primary/5 border-primary/20"
          }`} data-testid="true-apr-annotation">
            <div className="flex items-start gap-2 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-muted-foreground mb-0.5">
                  {trueApr.isEstimate ? t("floorplan:reader.aprAnn.estimatedTitle") : trueApr.isZeroInterest ? t("floorplan:reader.aprAnn.zeroTitle") : t("floorplan:reader.aprAnn.trueTitle")}
                </p>
                {trueApr.isZeroInterest ? (
                  <p className="text-xs text-sky-700 dark:text-sky-400">{t("floorplan:reader.aprAnn.zeroInterest")}</p>
                ) : trueApr.noInterestAccrued ? (
                  <p className="text-xs text-amber-700 dark:text-amber-400">{t("floorplan:reader.aprAnn.noInterest")}</p>
                ) : trueApr.insufficientData ? (
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    {t("floorplan:reader.aprAnn.insufficient")}
                    {trueApr.usedCount > 0 ? t("floorplan:reader.aprAnn.found", { count: trueApr.usedCount }) : ""}
                  </p>
                ) : trueApr.isEstimate ? (
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    {t("floorplan:reader.aprAnn.estimated")}
                  </p>
                ) : (
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="text-lg font-bold text-primary" data-testid="true-apr-value">
                      {trueApr.apr != null ? `${trueApr.apr.toFixed(4)}%` : "—"}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t("floorplan:reader.aprAnn.used", { count: trueApr.usedCount })}
                      {trueApr.excludedCount > 0 ? t("floorplan:reader.aprAnn.excludedSuffix", { count: trueApr.excludedCount }) : ""}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Editable Receivables Table */}
      <Separator />
      <EditableReceivablesSection
        rows={receivableRows}
        accuracyStatus={accuracyStatus}
        docInterest={accuracyInfo.interestDocTotal}
        docFees={accuracyInfo.feesDocTotal}
        showReceivables={showReceivables}
        onToggle={() => setShowReceivables(!showReceivables)}
        onEditRow={editRow}
        onDeleteRow={deleteRow}
        onAddRow={addRow}
      />

      {/* Action buttons */}
      <div className="flex gap-3 justify-end pt-2">
        {onSaveAsEntry && (
          <Button variant="outline" onClick={() => onSaveAsEntry(form)} data-testid="button-save-as-entry">
            <PlusCircle className="w-4 h-4 mr-2" />{t("floorplan:reader.saveAsEntry")}
          </Button>
        )}
        <Button onClick={handleSave} disabled={saving} data-testid="button-save-statement">
          {saving ? t("floorplan:reader.saving") : t("floorplan:reader.saveToHistory")}
        </Button>
      </div>
    </div>
  );
}

// ─── Accuracy Badge ───────────────────────────────────────────────────────────

interface AccuracyInfo {
  vehicleCount?: number;
  interestRowSum?: number;
  feesRowSum?: number;
  interestDocTotal?: number;
  feesDocTotal?: number;
  interestVariancePct?: number;
  feesVariancePct?: number;
  status?: "verified" | "warning" | "unverified" | "ai-corrected" | "needs-review";
  note?: string;
}

function AccuracyBadge({ accuracyCheckJson }: { accuracyCheckJson?: string | null }) {
  const { t } = useTranslation(["floorplan", "common"]);
  if (!accuracyCheckJson) return null;
  let info: AccuracyInfo;
  try { info = JSON.parse(accuracyCheckJson); } catch { return null; }

  const status = info.status ?? "unverified";
  const vehicleCount = info.vehicleCount ?? 0;

  if (status === "verified") {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex items-center gap-1 text-xs text-green-600 font-medium cursor-default" data-testid="badge-accuracy-verified">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>{vehicleCount}u</span>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[220px] text-xs">
            <p className="font-semibold text-green-700 mb-0.5">{t("floorplan:reader.acc.verifiedTitle")}</p>
            <p>{t("floorplan:reader.acc.extracted", { count: vehicleCount })}</p>
            {info.note && <p className="mt-0.5 text-muted-foreground">{info.note}</p>}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (status === "ai-corrected") {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex items-center gap-1 text-xs text-green-600 font-medium cursor-default" data-testid="badge-accuracy-ai-corrected">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>{vehicleCount}u ✓</span>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[260px] text-xs">
            <p className="font-semibold text-green-700 mb-0.5">{t("floorplan:reader.acc.aiCorrectedTitle")}</p>
            <p>{t("floorplan:reader.acc.aiCorrectedBody", { count: vehicleCount })}</p>
            {info.note && <p className="mt-1 text-muted-foreground">{info.note}</p>}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (status === "needs-review") {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex items-center gap-1 text-xs text-yellow-600 font-medium cursor-default" data-testid="badge-accuracy-needs-review">
              <ShieldAlert className="w-3.5 h-3.5" />
              <span>{vehicleCount}u !</span>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[280px] text-xs">
            <p className="font-semibold text-yellow-700 mb-0.5">{t("floorplan:reader.acc.needsReviewTitle")}</p>
            <p>{t("floorplan:reader.acc.needsReviewBody", { count: vehicleCount })}</p>
            {info.note && <p className="mt-1 text-yellow-800">{info.note}</p>}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (status === "warning") {
    const maxVar = Math.max(info.interestVariancePct ?? 0, info.feesVariancePct ?? 0);
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex items-center gap-1 text-xs text-yellow-600 font-medium cursor-default" data-testid="badge-accuracy-warning">
              <ShieldAlert className="w-3.5 h-3.5" />
              <span>{vehicleCount}u</span>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[240px] text-xs">
            <p className="font-semibold text-yellow-700 mb-0.5">{t("floorplan:reader.acc.minorVariance", { pct: maxVar.toFixed(1) })}</p>
            <p>{t("floorplan:reader.acc.extracted", { count: vehicleCount })}</p>
            {info.note && <p className="mt-0.5 text-muted-foreground">{info.note}</p>}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="flex items-center gap-1 text-xs text-muted-foreground cursor-default" data-testid="badge-accuracy-unverified">
            <ShieldOff className="w-3.5 h-3.5" />
            <span>{vehicleCount}u</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[240px] text-xs">
          <p className="font-semibold mb-0.5">{t("floorplan:reader.acc.notCrossChecked")}</p>
          <p>{t("floorplan:reader.acc.extracted", { count: vehicleCount })}</p>
          {info.note && <p className="mt-0.5 text-muted-foreground">{info.note}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ─── Statement History ────────────────────────────────────────────────────────

function StatementHistory({ onViewStatement }: { onViewStatement: (stmt: any) => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation(["floorplan", "common"]);

  const { data: statements = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/parsed-statements"],
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/parsed-statements/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/parsed-statements"] });
      toast({ title: t("floorplan:reader.statementDeleted") });
    },
  });

  if (isLoading) return <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}</div>;

  if (statements.length === 0) {
    return (
      <div className="py-12 text-center">
        <History className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-40" />
        <p className="text-sm text-muted-foreground">{t("floorplan:reader.noSaved")}</p>
        <p className="text-xs text-muted-foreground/60 mt-1">{t("floorplan:reader.noSavedHint")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {statements.map((stmt) => (
        <div
          key={stmt.id}
          className="flex items-center justify-between p-3 rounded-lg border bg-card gap-3"
          data-testid={`statement-history-${stmt.id}`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
              <FileText className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{stmt.lenderName || t("floorplan:reader.unknownLender")}</p>
              <p className="text-xs text-muted-foreground truncate">
                {stmt.fileName}
                {stmt.statementPeriodStart && ` • ${stmt.statementPeriodStart}`}
                {stmt.status === "pending_review" && (
                  <span className="ml-1.5 text-yellow-600">{t("floorplan:reader.pendingReview")}</span>
                )}
                {stmt.status === "saved" && (
                  <span className="ml-1.5 text-chart-3">{t("floorplan:reader.savedStatus")}</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {stmt.totalInterestPaid && (
              <Badge variant="secondary" className="text-xs">
                {t("floorplan:reader.interestBadge", { amount: parseFloat(stmt.totalInterestPaid).toLocaleString("en-US", { maximumFractionDigits: 0 }) })}
              </Badge>
            )}
            <AccuracyBadge accuracyCheckJson={stmt.accuracyCheckJson} />
            <Button
              size="icon" variant="ghost"
              onClick={() => onViewStatement(stmt)}
              data-testid={`button-view-statement-${stmt.id}`}
            >
              <Eye className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="icon" variant="ghost"
              onClick={() => deleteMutation.mutate(stmt.id)}
              disabled={deleteMutation.isPending}
              data-testid={`button-delete-statement-${stmt.id}`}
            >
              <Trash2 className="w-3.5 h-3.5 text-destructive" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main StatementReader dialog ──────────────────────────────────────────────

interface StatementReaderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaveAsFloorplanEntry?: (data: ReviewForm) => void;
  onMinimize?: () => void;
}

export function StatementReader({ open, onOpenChange, onSaveAsFloorplanEntry, onMinimize }: StatementReaderProps) {
  const [tab, setTab] = useState<"upload" | "history">("upload");
  const [parseResult, setParseResult] = useState<ParsedResult | null>(null);
  const [viewingStatement, setViewingStatement] = useState<any | null>(null);
  const [failedError, setFailedError] = useState<string | null>(null);
  const qc = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation(["floorplan", "financial", "common"]);
  const scanner = useScannerContext();

  // When opened: handle completed result or failed state from a background scan.
  // Hydrate whenever the scanner has pending results — whether the user came back
  // via the "Review now" toast (reopenRequested=true) or by manually reopening
  // after dismissing the pill (dismissed=true but reopenRequested=false).
  useEffect(() => {
    if (!open) return;
    if (scanner.status === "failed" && scanner.reopenRequested) {
      setFailedError(scanner.errorMessage || t("floorplan:reader.extractionFailedRetry"));
      scanner.clearReopenRequest();
    } else if (scanner.status === "completed" && scanner.result) {
      setFailedError(null);
      setParseResult(scanner.result as ParsedResult);
      setViewingStatement(null);
      if (scanner.reopenRequested) scanner.clearReopenRequest();
      qc.invalidateQueries({ queryKey: ["/api/parsed-statements"], refetchType: "all" });
    }
  }, [open, scanner.reopenRequested, scanner.status, scanner.result, scanner.errorMessage]);

  // While opened in "background scanning" state: auto-transition when context completes
  // Note: isBackgroundMode is false once status leaves "scanning", so transitions to
  // "completed" or "failed" are handled by the first useEffect above via reopenRequested.
  const isBackgroundMode = open && scanner.reopenRequested && scanner.status === "scanning";
  useEffect(() => {
    if (isBackgroundMode && scanner.status === "completed" && scanner.result) {
      setFailedError(null);
      setParseResult(scanner.result as ParsedResult);
      setViewingStatement(null);
      scanner.clearReopenRequest();
      qc.invalidateQueries({ queryKey: ["/api/parsed-statements"], refetchType: "all" });
    }
  }, [isBackgroundMode, scanner.status, scanner.result]);

  const handleUploadComplete = (result: ParsedResult) => {
    setParseResult(result);
    qc.invalidateQueries({ queryKey: ["/api/parsed-statements"], refetchType: "all" });
  };

  const handleSave = async (form: ReviewForm, statementId: string) => {
    // Clear background scan context now that user is saving the result
    scanner.clear();
    await apiRequest("PATCH", `/api/parsed-statements/${statementId}`, {
      lenderName: form.lenderName,
      statementPeriodStart: form.statementPeriodStart,
      statementPeriodEnd: form.statementPeriodEnd,
      statementMonth: form.statementMonth ? parseInt(form.statementMonth) : null,
      statementYear: form.statementYear ? parseInt(form.statementYear) : null,
      totalInterestPaid: form.totalInterestPaid || null,
      totalFeesPaid: form.totalFeesPaid || null,
      averageBalance: form.averageBalance || null,
      unitsSold: form.unitsSold ? parseInt(form.unitsSold) : null,
      periodDays: form.periodDays ? parseInt(form.periodDays) : null,
      avgDaysInStock: form.avgDaysInStock || null,
      feeAdmin: form.feeAdmin || null,
      feeAudit: form.feeAudit || null,
      feeCurtailment: form.feeCurtailment || null,
      feeDoc: form.feeDoc || null,
      feeTitle: form.feeTitle || null,
      feeOther: form.feeOther || null,
      notes: form.notes,
      receivablesJson: form.receivablesJson ?? null,
      status: "saved",
    });
    qc.invalidateQueries({ queryKey: ["/api/parsed-statements"], refetchType: "all" });
    qc.invalidateQueries({ queryKey: ["/api/lenders"], refetchType: "all" });
    qc.invalidateQueries({ queryKey: ["/api/floorplan-inventory"], refetchType: "all" });
    qc.invalidateQueries({
      predicate: (q) => typeof q.queryKey[0] === "string" && q.queryKey[0].startsWith("/api/lenders/comparison-stats"),
      refetchType: "all",
    });
    setTab("history");
    setParseResult(null);
  };

  const handleSaveAsEntry = (form: ReviewForm) => {
    onSaveAsFloorplanEntry?.(form);
    onOpenChange(false);
    toast({ title: t("floorplan:reader.populatingTitle"), description: t("floorplan:reader.populatingDesc") });
  };

  const handleViewStatement = (stmt: any) => {
    const reconstructed: ParsedResult = {
      statement: stmt,
      parsed: {
        lenderName: stmt.lenderName ?? "",
        lenderKey: stmt.lenderKey ?? "generic",
        statementPeriod: { start: stmt.statementPeriodStart, end: stmt.statementPeriodEnd },
        totals: {
          totalInterestPaid: parseFloat(stmt.totalInterestPaid || "0"),
          totalFeesPaid: parseFloat(stmt.totalFeesPaid || "0"),
          averageBalance: parseFloat(stmt.averageBalance || "0"),
          unitsSold: stmt.unitsSold ?? 0,
          periodDays: stmt.periodDays ?? 30,
          avgDaysInStock: parseFloat(stmt.avgDaysInStock || "0"),
        },
        feeBreakdown: {
          feeAdmin: parseFloat(stmt.feeAdmin || "0"),
          feeAudit: parseFloat(stmt.feeAudit || "0"),
          feeCurtailment: parseFloat(stmt.feeCurtailment || "0"),
          feeDoc: parseFloat(stmt.feeDoc || "0"),
          feeTitle: parseFloat(stmt.feeTitle || "0"),
          feeOther: parseFloat(stmt.feeOther || "0"),
        },
        receivables: JSON.parse(stmt.receivablesJson || "[]"),
        detectedFieldsConfidence: JSON.parse(stmt.confidenceJson || "{}"),
        warnings: JSON.parse(stmt.warningsJson || "[]"),
      },
      extractionMethod: stmt.extractionMethod ?? "manual-required",
      success: true,
    };
    setViewingStatement(reconstructed);
  };

  // Determine if we're in background-scanning mode (re-opened while scan still running)
  const isBackgroundScanMode = open && scanner.reopenRequested && scanner.status === "scanning";

  const handleMinimize = () => {
    onOpenChange(false);
    onMinimize?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setParseResult(null); setViewingStatement(null); setFailedError(null); } }}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-y-auto"
        hideClose={!!parseResult}
        onInteractOutside={parseResult ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={parseResult ? (e) => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-primary" />
            {t("floorplan:reader.dialogTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("floorplan:reader.dialogDesc")}
          </DialogDescription>
        </DialogHeader>

        {isBackgroundScanMode ? (
          <div className="flex flex-col items-center justify-center py-12 gap-4 text-center">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <RefreshCw className="w-7 h-7 text-primary animate-spin" />
            </div>
            <div>
              <p className="text-base font-semibold">{t("floorplan:reader.bgScanning")}</p>
              <p className="text-sm text-muted-foreground mt-1">{t("floorplan:reader.bgScanningHint")}</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleMinimize} data-testid="button-minimize-background">
              {t("floorplan:reader.minimizeAgain")}
            </Button>
          </div>
        ) : failedError ? (
          <div className="space-y-4" data-testid="panel-scan-failed">
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-destructive">{t("floorplan:reader.parsingFailed")}</p>
                <p className="text-sm text-muted-foreground mt-0.5">{failedError}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0 h-7 w-7 p-0"
                onClick={() => { setFailedError(null); scanner.clear(); }}
                data-testid="button-dismiss-scan-error"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              {t("floorplan:reader.uploadDifferent")}
            </p>
            <ErrorBoundary label={t("floorplan:reader.uploadExtraction")}>
              <UploadDropzone
                onUploadStart={() => setFailedError(null)}
                onUploadComplete={handleUploadComplete}
                onMinimize={handleMinimize}
              />
            </ErrorBoundary>
          </div>
        ) : viewingStatement ? (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Button variant="ghost" size="sm" onClick={() => setViewingStatement(null)}>
                {t("floorplan:reader.backToHistory")}
              </Button>
            </div>
            <ReviewPanel
              result={viewingStatement}
              onSave={handleSave}
              onSaveAsEntry={handleSaveAsEntry}
            />
          </div>
        ) : parseResult ? (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="w-5 h-5 text-chart-3" />
              <span className="text-sm font-medium text-chart-3">{t("financial:statementReader.parsed")}</span>
              <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setParseResult(null)}>
                {t("financial:statementReader.uploadAnother")}
              </Button>
            </div>
            <ReviewPanel
              result={parseResult}
              onSave={handleSave}
              onSaveAsEntry={handleSaveAsEntry}
            />
          </div>
        ) : (
          <Tabs value={tab} onValueChange={(v) => { setTab(v as any); trackEvent("tab_change", "/floorplan", { tab: v }); }}>
            <TabsList>
              <TabsTrigger value="upload" data-testid="tab-upload">
                <Upload className="w-3.5 h-3.5 mr-1.5" />{t("financial:statementReader.uploadStatement")}
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history">
                <History className="w-3.5 h-3.5 mr-1.5" />{t("financial:statementReader.statementHistory")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="upload" className="mt-4">
              <ErrorBoundary label={t("floorplan:reader.uploadExtraction")}>
                <UploadDropzone onUploadComplete={handleUploadComplete} onMinimize={handleMinimize} />
              </ErrorBoundary>
            </TabsContent>
            <TabsContent value="history" className="mt-4">
              <StatementHistory onViewStatement={handleViewStatement} />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
